/* Lumetier · Landing page — premium dark SaaS redesign.
   Full dark aesthetic: deep near-black canvas, gold accents,
   glass-style cards, gradient hero text, modern nav.
   Business logic / data-testid attributes preserved throughout. */

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Camera, Heart, FileSignature, ImagePlus, CreditCard, Star, Calendar,
  Check, Sparkles, ArrowRight, Clock,
} from "lucide-react";
import LumetierLogo from "@/components/LumetierLogo";
import InteractiveGalleryDemo from "@/pages/lumetier/InteractiveGalleryDemo";
import InteractiveSiteDemo from "@/pages/lumetier/InteractiveSiteDemo";
import ProductTour from "@/pages/lumetier/ProductTour";

const GOLD = "#C9A961";
const GOLD_LIGHT = "#EDD78C";

// ── Scroll-reveal ─────────────────────────────────────────────────────────────
function useReveal() {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } },
      { threshold: 0.12 }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  return { ref, shown };
}

function Reveal({ children, delay = 0, className = "" }) {
  const { ref, shown } = useReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "translateY(0)" : "translateY(20px)",
        transition: `opacity .85s cubic-bezier(0.16,1,0.3,1) ${delay}s, transform .85s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}

// ── Static data ───────────────────────────────────────────────────────────────
const FEATURES = [
  { icon: Calendar, name: "Bookings & calendar", body: "Clients pick a service, a date, and a time — then pay a deposit before the slot is reserved. No more chasing." },
  { icon: ImagePlus, name: "Branded galleries", body: "Beautiful proofing galleries with favorites, comments, and password-protected downloads. Your name on the URL, not ours." },
  { icon: FileSignature, name: "Contracts & e-signature", body: "Reusable templates · Auto-fill from booking · One-tap signing on any device · PDF saved to the client record." },
  { icon: CreditCard, name: "Invoices & payments", body: "Send an invoice, accept a deposit, mark a balance paid — all in one record. Zelle, Stripe, or your own flow." },
  { icon: Star, name: "Auto-review requests", body: "The day after delivery, your happiest clients get a one-tap review prompt. Build your social proof on autopilot." },
  { icon: Clock, name: "Reminders that show up", body: "Email AND push notifications for upcoming sessions, balances due, and gallery deadlines. Native-app feel from your browser." },
];

const COMPARISON = [
  { feature: "Bookings + deposits", lumetier: true, honeybook: true, dubsado: true, shootproof: false },
  { feature: "Native client galleries", lumetier: true, honeybook: false, dubsado: false, shootproof: true },
  { feature: "Auto-review requests", lumetier: true, honeybook: false, dubsado: false, shootproof: false },
  { feature: "Installable mobile app (PWA)", lumetier: true, honeybook: false, dubsado: false, shootproof: false },
  { feature: "Built by a working photographer", lumetier: true, honeybook: false, dubsado: false, shootproof: false },
  { feature: "Sub-$30/mo starter tier", lumetier: true, honeybook: false, dubsado: false, shootproof: true },
];

const PRICING = [
  {
    name: "Solo", price: 29,
    line: "For independent photographers, just starting out or running on the side.",
    features: ["1 user", "Up to 20 bookings/mo", "5 active galleries", "Contracts & e-sign", "Email + push reminders"],
    highlight: false,
  },
  {
    name: "Studio", price: 69,
    line: "For full-time photographers running 20–80 sessions a month.",
    features: ["Unlimited bookings", "Unlimited galleries", "Custom branded URL", "Resend / Stripe / Zelle", "Priority support"],
    highlight: true,
  },
  {
    name: "Pro", price: 129,
    line: "For multi-shooter studios or agencies serving 100+ clients/mo.",
    features: ["3 team seats", "White-label client portal", "Advanced reporting", "API access (coming soon)", "Dedicated onboarding"],
    highlight: false,
  },
];

// ── Hero with mouse-tied parallax on the gold radial layers ──────────────────
function HeroSection() {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Respect users who prefer no motion
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    let raf = 0;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const nx = ((e.clientX - rect.left) / rect.width - 0.5) * 2;   // -1..1
      const ny = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setPos({ x: nx, y: ny }));
    };
    const onLeave = () => setPos({ x: 0, y: 0 });

    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
      cancelAnimationFrame(raf);
    };
  }, []);

  // Different depth multipliers per layer for true parallax feel
  const layer = (depth) => ({
    transform: `translate3d(${pos.x * depth}px, ${pos.y * depth}px, 0)`,
    transition: "transform .6s cubic-bezier(0.16,1,0.3,1)",
    willChange: "transform",
  });

  return (
    <section
      ref={ref}
      className="relative min-h-[90vh] flex flex-col items-center justify-center overflow-hidden"
    >
      {/* Parallax background gradient decorations */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute -top-20 left-1/2 -translate-x-1/2 w-[900px] h-[600px]"
          style={{
            background: "radial-gradient(ellipse, rgba(201,169,97,0.13) 0%, transparent 65%)",
            ...layer(28),
          }}
        />
        <div
          className="absolute top-1/2 left-1/4 w-[500px] h-[400px]"
          style={{
            background: "radial-gradient(ellipse, rgba(74,159,212,0.07) 0%, transparent 65%)",
            ...layer(-18),
          }}
        />
        <div
          className="absolute bottom-0 right-1/4 w-[400px] h-[300px]"
          style={{
            background: "radial-gradient(ellipse, rgba(201,169,97,0.06) 0%, transparent 60%)",
            ...layer(40),
          }}
        />
      </div>

      {/* Subtle grid texture (static — gives parallax a fixed frame to play against) */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.022) 1px, transparent 1px), linear-gradient(to right, rgba(255,255,255,0.022) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      <div className="relative z-10 text-center px-6 max-w-5xl mx-auto pt-16 pb-24">
        <Reveal>
          <LumetierLogo size={180} inverted accent={GOLD} />
        </Reveal>

        <Reveal delay={0.35}>
          <div
            className="inline-flex items-center gap-2 px-3.5 h-7 mt-12 text-[10px] uppercase tracking-[0.3em]"
            style={{
              background: `${GOLD}18`,
              border: `1px solid ${GOLD}35`,
              color: GOLD,
            }}
          >
            ✦ Now in private beta for photographers ✦
          </div>
        </Reveal>

        <Reveal delay={0.55}>
          <h1
            className="text-5xl sm:text-6xl md:text-[80px] lg:text-[96px] font-light leading-[0.92] tracking-tight mt-8 max-w-4xl mx-auto"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            The operating system for{" "}
            <em
              className="italic not-italic"
              style={{
                backgroundImage: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_LIGHT} 45%, ${GOLD} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                fontStyle: "italic",
              }}
            >
              photographers
            </em>
            .
          </h1>
        </Reveal>

        <Reveal delay={0.75}>
          <p className="text-lg md:text-xl text-stone-400 mt-7 max-w-2xl mx-auto leading-relaxed">
            Bookings, contracts, galleries, payments, reviews — beautifully managed in one place.
            Built by a photographer who got tired of using five different tools.
          </p>
        </Reveal>

        <Reveal delay={0.95}>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-10">
            <a
              href="#customize"
              className="inline-flex items-center gap-2.5 px-7 h-12 text-xs uppercase tracking-[0.22em] text-stone-900 font-medium hover:opacity-90 transition-opacity group"
              style={{ background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})` }}
            >
              Try the gallery
              <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
            </a>
            <a
              href="#waitlist"
              className="inline-flex items-center gap-2 px-7 h-12 border border-white/25 text-xs uppercase tracking-[0.22em] text-stone-200 hover:border-white/60 hover:text-white transition-colors"
            >
              Join the waitlist
            </a>
          </div>
        </Reveal>

        <Reveal delay={1.15}>
          <a
            href="https://allure-image.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-14 text-[11px] text-stone-600 hover:text-stone-300 transition-colors uppercase tracking-[0.22em]"
          >
            Made by <strong className="text-stone-400">Allure Image</strong> · a working photography studio
          </a>
        </Reveal>
      </div>
    </section>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function LumetierLanding() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [photogType, setPhotogType] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    if (!email || !name) return toast.error("Please enter your name and email");
    setSubmitting(true);
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${apiBase}/api/lumetier/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), photographer_type: photogType || null, source: "landing" }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.detail || "Couldn't save — please try again.");
      }
      setSubmitted(true);
      toast.success("You're on the list. Charter pricing locked in.");
    } catch (err) {
      toast.error(err.message || "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="min-h-screen bg-[#070709] text-stone-100"
      style={{ fontFamily: "ui-sans-serif, system-ui, -apple-system, sans-serif" }}
    >
      {/* ── NAV ──────────────────────────────────────────────────────────── */}
      <nav className="sticky top-0 z-30 backdrop-blur-xl bg-[#070709]/90 border-b border-white/[0.07]">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link
            to="/lumetier"
            className="text-lg font-light tracking-[0.42em] text-stone-100"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            L U M E T I E R
          </Link>
          <div className="flex items-center gap-7 text-[11px] uppercase tracking-[0.22em]">
            <a href="#features" className="hidden sm:inline text-stone-400 hover:text-stone-100 transition-colors">Features</a>
            <a href="#customize" className="hidden sm:inline transition-colors" style={{ color: GOLD }}>Try it</a>
            <a href="#pricing" className="hidden sm:inline text-stone-400 hover:text-stone-100 transition-colors">Pricing</a>
            <a
              href="#waitlist"
              className="inline-flex items-center gap-1.5 px-4 h-9 text-xs uppercase tracking-[0.22em] text-stone-900 font-medium transition-opacity hover:opacity-85"
              style={{ background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})` }}
            >
              Join waitlist
            </a>
          </div>
        </div>
      </nav>

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <HeroSection />

      {/* ── TRUST STRIP ──────────────────────────────────────────────────── */}
      <section className="border-y border-white/[0.07] bg-white/[0.02]">
        <div className="max-w-6xl mx-auto px-6 py-12 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { n: "5+", l: "Years shooting" },
            { n: "300+", l: "Sessions delivered" },
            { n: "100%", l: "Built in-house" },
            { n: "1", l: "Tool for everything" },
          ].map((s, i) => (
            <Reveal key={s.l} delay={i * 0.1}>
              <div>
                <div
                  className="text-3xl md:text-4xl font-light"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: GOLD }}
                >
                  {s.n}
                </div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-stone-500 mt-1.5">{s.l}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── PAIN POINTS ──────────────────────────────────────────────────── */}
      <section className="max-w-4xl mx-auto px-6 py-28 text-center">
        <Reveal>
          <div className="text-[11px] uppercase tracking-[0.32em] text-stone-500 mb-5">Why now</div>
          <h2
            className="text-4xl md:text-5xl font-light leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            You started shooting because you love the craft.
            <br />
            <span className="text-stone-400">Not the admin.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <div className="grid sm:grid-cols-3 gap-4 mt-16 text-left">
            {[
              { n: "01", h: "5+ tools", b: "HoneyBook for contracts. Pixieset for galleries. Square for payments. Calendly for booking. Mailchimp for reminders. Sound familiar?" },
              { n: "02", h: "$200+/mo", b: "Subscriptions add up fast. You're paying for ten things and using two features each." },
              { n: "03", h: "Hours lost", b: "Every Friday night reconciling who paid, who didn't, what got delivered, what's still owed. There's a better way." },
            ].map((p) => (
              <div
                key={p.h}
                className="bg-white/[0.025] border border-white/[0.08] p-7 hover:border-white/[0.16] transition-colors"
              >
                <div
                  className="text-3xl font-light mb-3"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: GOLD }}
                >
                  {p.n}
                </div>
                <div className="font-medium text-stone-100 mb-2">{p.h}</div>
                <div className="text-sm text-stone-400 leading-relaxed">{p.b}</div>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* ── FEATURES ─────────────────────────────────────────────────────── */}
      <section id="features" className="border-t border-white/[0.07] py-28">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal>
            <div className="text-center mb-16">
              <div className="text-[11px] uppercase tracking-[0.32em] text-stone-500 mb-5">Everything you need</div>
              <h2
                className="text-4xl md:text-5xl font-light"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
              >
                Six tools,{" "}
                <em
                  className="italic"
                  style={{
                    backgroundImage: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  one place.
                </em>
              </h2>
              <div className="h-px w-14 mx-auto mt-7" style={{ background: `linear-gradient(to right, transparent, ${GOLD}, transparent)` }} />
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <Reveal key={f.name} delay={i * 0.07}>
                  <div className="bg-white/[0.03] border border-white/[0.08] p-7 h-full hover:bg-white/[0.06] hover:border-white/[0.16] transition-all group cursor-default">
                    <div
                      className="w-11 h-11 flex items-center justify-center mb-6"
                      style={{ background: `${GOLD}18`, color: GOLD }}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3
                      className="text-2xl font-light mb-3 text-stone-100"
                      style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                    >
                      {f.name}
                    </h3>
                    <p className="text-sm text-stone-400 leading-relaxed">{f.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── PRODUCT TOUR ─────────────────────────────────────────────────── */}
      <ProductTour dark />

      {/* ── DEMOS ────────────────────────────────────────────────────────── */}
      <InteractiveSiteDemo dark />
      <InteractiveGalleryDemo dark />

      {/* ── REAL PRODUCT PROOF ───────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-6 py-28">
        <Reveal>
          <div className="text-center mb-14">
            <div className="text-[11px] uppercase tracking-[0.32em] text-stone-500 mb-5">A real working product</div>
            <h2
              className="text-4xl md:text-5xl font-light"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              Not a prototype.
              <br />
              <span className="text-stone-400">A studio that runs on it.</span>
            </h2>
            <p className="text-stone-500 mt-6 max-w-xl mx-auto text-sm leading-relaxed">
              Lumetier is the same engine that runs{" "}
              <a href="https://allure-image.com" target="_blank" rel="noopener noreferrer" className="underline text-stone-300 hover:text-white">
                Allure Image
              </a>
              &apos;s full-time photography business. Every feature was battle-tested on real clients before you&apos;ll ever see it.
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.3}>
          {/* Stylized dashboard mockup */}
          <div className="mx-auto max-w-4xl border border-white/[0.12] bg-[#0D0F14]">
            {/* Fake browser chrome */}
            <div className="flex items-center gap-1.5 px-4 h-10 border-b border-white/[0.08]">
              <div className="w-2.5 h-2.5 rounded-full bg-stone-700" />
              <div className="w-2.5 h-2.5 rounded-full bg-stone-700" />
              <div className="w-2.5 h-2.5 rounded-full bg-stone-700" />
              <div className="ml-3 text-[11px] text-stone-600 font-mono">studio.lumetier.studio/admin</div>
            </div>
            {/* Fake dark sidebar + content */}
            <div className="flex">
              <div className="w-48 shrink-0 bg-[#080A0E] border-r border-white/[0.07] p-4 hidden md:block">
                <div className="space-y-1">
                  {["Overview", "Bookings", "Galleries", "Invoices", "Contracts"].map((l, i) => (
                    <div
                      key={l}
                      className={`px-3 py-2 text-xs rounded-sm ${i === 0 ? "text-stone-100 bg-white/[0.08]" : "text-stone-500"}`}
                      style={i === 0 ? { borderLeft: `2px solid ${GOLD}` } : {}}
                    >
                      {l}
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex-1 p-5 grid grid-cols-2 sm:grid-cols-4 gap-3 text-stone-100">
                {[
                  { label: "Upcoming sessions", val: "6", sub: "this week", color: "border-t-blue-500/50" },
                  { label: "Pending bookings", val: "7", sub: "awaiting deposit", color: "border-t-amber-500/50" },
                  { label: "Revenue (mo)", val: "$4,725", sub: "+18% vs last", color: "border-t-emerald-500/50" },
                  { label: "Open invoices", val: "1", sub: "due Friday", color: "border-t-violet-500/50" },
                ].map((s) => (
                  <div key={s.label} className={`bg-white/[0.03] border border-white/[0.08] border-t-2 ${s.color} p-4`}>
                    <div className="text-[9px] uppercase tracking-[0.2em] text-stone-500">{s.label}</div>
                    <div
                      className="text-2xl font-light mt-1 text-stone-100"
                      style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                    >
                      {s.val}
                    </div>
                    <div className="text-[10px] text-stone-600 mt-1">{s.sub}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ── COMPARISON ───────────────────────────────────────────────────── */}
      <section className="border-t border-white/[0.07] py-28">
        <div className="max-w-4xl mx-auto px-6">
          <Reveal>
            <div className="text-center mb-14">
              <div className="text-[11px] uppercase tracking-[0.32em] text-stone-500 mb-5">How we compare</div>
              <h2
                className="text-4xl md:text-5xl font-light"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
              >
                One tool. Less spend. More craft.
              </h2>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b border-white/[0.1]">
                    <th className="text-left py-4 px-3 font-normal text-stone-500 text-[11px] uppercase tracking-[0.18em]">Feature</th>
                    <th className="py-4 px-3 font-semibold text-sm" style={{ color: GOLD }}>Lumetier</th>
                    <th className="py-4 px-3 font-normal text-stone-500 text-[11px] uppercase tracking-[0.14em]">HoneyBook</th>
                    <th className="py-4 px-3 font-normal text-stone-500 text-[11px] uppercase tracking-[0.14em]">Dubsado</th>
                    <th className="py-4 px-3 font-normal text-stone-500 text-[11px] uppercase tracking-[0.14em]">ShootProof</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON.map((r) => (
                    <tr key={r.feature} className="border-b border-white/[0.06] hover:bg-white/[0.02] transition-colors">
                      <td className="py-3.5 px-3 text-stone-300">{r.feature}</td>
                      <td className="py-3.5 px-3 text-center">{r.lumetier ? <Check className="w-4 h-4 inline" style={{ color: GOLD }} /> : <span className="text-stone-700">—</span>}</td>
                      <td className="py-3.5 px-3 text-center">{r.honeybook ? <Check className="w-4 h-4 inline text-stone-500" /> : <span className="text-stone-700">—</span>}</td>
                      <td className="py-3.5 px-3 text-center">{r.dubsado ? <Check className="w-4 h-4 inline text-stone-500" /> : <span className="text-stone-700">—</span>}</td>
                      <td className="py-3.5 px-3 text-center">{r.shootproof ? <Check className="w-4 h-4 inline text-stone-500" /> : <span className="text-stone-700">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── PRICING ──────────────────────────────────────────────────────── */}
      <section id="pricing" className="max-w-6xl mx-auto px-6 py-28">
        <Reveal>
          <div className="text-center mb-14">
            <div className="text-[11px] uppercase tracking-[0.32em] text-stone-500 mb-5">Charter pricing</div>
            <h2
              className="text-4xl md:text-5xl font-light"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
            >
              Honest pricing.
              <br />
              <span className="text-stone-400">Locked in for life.</span>
            </h2>
            <p className="text-stone-500 mt-6 max-w-xl mx-auto text-sm leading-relaxed">
              Join the waitlist now and your first 24 months are locked at charter pricing. We won&apos;t raise it on you.
            </p>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-4 mt-12">
          {PRICING.map((tier, i) => (
            <Reveal key={tier.name} delay={i * 0.1}>
              <div
                className="flex flex-col h-full p-8"
                style={
                  tier.highlight
                    ? {
                        background: "linear-gradient(#0D0F14, #0D0F14) padding-box, linear-gradient(135deg, #C9A961, #EDD78C, #C9A961) border-box",
                        border: "1px solid transparent",
                      }
                    : {
                        background: "#0D0F14",
                        border: "1px solid rgba(255,255,255,0.08)",
                      }
                }
              >
                {tier.highlight && (
                  <div className="text-[10px] uppercase tracking-[0.28em] mb-3" style={{ color: GOLD }}>
                    ★ Most popular
                  </div>
                )}
                <h3
                  className="text-3xl font-light text-stone-100"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                >
                  {tier.name}
                </h3>
                <div className="mt-5 mb-5">
                  <span
                    className="text-5xl font-light text-stone-100"
                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                  >
                    ${tier.price}
                  </span>
                  <span className="text-stone-500 text-sm">/mo</span>
                </div>
                <p className="text-sm text-stone-500 mb-7 leading-relaxed flex-grow-0">{tier.line}</p>
                <ul className="space-y-3 mb-8 text-sm flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: GOLD }} />
                      <span className="text-stone-300">{f}</span>
                    </li>
                  ))}
                </ul>
                <a
                  href="#waitlist"
                  className={`text-center px-4 h-11 inline-flex items-center justify-center text-xs uppercase tracking-[0.22em] transition-all ${
                    tier.highlight
                      ? "text-stone-900 font-medium hover:opacity-90"
                      : "border border-white/20 text-stone-300 hover:border-white/50 hover:text-white"
                  }`}
                  style={tier.highlight ? { background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})` } : {}}
                >
                  Join waitlist
                </a>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ── WAITLIST ─────────────────────────────────────────────────────── */}
      <section id="waitlist" className="border-t border-white/[0.07] py-28">
        <div className="max-w-xl mx-auto px-6">
          {!submitted ? (
            <>
              <Reveal>
                <div className="text-center mb-12">
                  <Sparkles className="w-7 h-7 mx-auto mb-5" style={{ color: GOLD }} />
                  <div className="text-[11px] uppercase tracking-[0.32em] text-stone-500 mb-5">Charter list</div>
                  <h2
                    className="text-4xl md:text-5xl font-light text-stone-100"
                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                  >
                    Be one of the first 100.
                  </h2>
                  <p className="text-stone-500 mt-6 leading-relaxed text-sm">
                    Charter members get <span className="text-stone-200 font-medium">2 years</span> at the launch price (no increases), priority onboarding, and a direct line to me as we shape the product.
                  </p>
                </div>
              </Reveal>

              <Reveal delay={0.3}>
                <form
                  onSubmit={onSubmit}
                  className="bg-white/[0.03] border border-white/[0.08] p-8 space-y-6"
                  data-testid="waitlist-form"
                >
                  {[
                    { label: "Your name", value: name, onChange: setName, type: "text", testid: "wl-name", placeholder: "Jane Smith" },
                    { label: "Email", value: email, onChange: setEmail, type: "email", testid: "wl-email", placeholder: "jane@studio.com" },
                  ].map(({ label, value, onChange, type, testid, placeholder }) => (
                    <label key={label} className="block">
                      <span className="text-[10px] uppercase tracking-[0.22em] text-stone-500">{label}</span>
                      <input
                        type={type}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={placeholder}
                        className="mt-2 w-full bg-transparent border-b border-white/[0.15] py-3 outline-none focus:border-[#C9A961] placeholder:text-stone-700 text-stone-100 text-base transition-colors"
                        data-testid={testid}
                      />
                    </label>
                  ))}

                  <label className="block">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-stone-500">What kind of photographer are you?</span>
                    <select
                      value={photogType}
                      onChange={(e) => setPhotogType(e.target.value)}
                      className="mt-2 w-full bg-[#0D0F14] border-b border-white/[0.15] py-3 outline-none focus:border-[#C9A961] text-stone-300 text-base transition-colors"
                      data-testid="wl-type"
                    >
                      <option value="">Select one…</option>
                      <option value="wedding">Wedding</option>
                      <option value="family">Family / portrait</option>
                      <option value="newborn">Newborn</option>
                      <option value="branding">Branding / commercial</option>
                      <option value="event">Event</option>
                      <option value="other">Other</option>
                    </select>
                  </label>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="wl-submit"
                    className="w-full px-5 h-12 text-xs uppercase tracking-[0.22em] text-stone-900 font-medium hover:opacity-90 disabled:opacity-50 transition-opacity mt-2"
                    style={{ background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})` }}
                  >
                    {submitting ? "Saving…" : "Lock in my charter spot →"}
                  </button>

                  <p className="text-[11px] text-stone-600 text-center pt-1">
                    No spam. One launch announcement when we go live. Unsubscribe anytime.
                  </p>
                </form>
              </Reveal>
            </>
          ) : (
            <Reveal>
              <div className="text-center bg-white/[0.03] border border-white/[0.1] p-14" data-testid="waitlist-success">
                <Heart className="w-10 h-10 mx-auto mb-6 fill-current" style={{ color: GOLD }} />
                <h2
                  className="text-4xl font-light mb-5 text-stone-100"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
                >
                  You&apos;re on the list.
                </h2>
                <p className="text-stone-400 leading-relaxed mb-6 text-sm">
                  Your charter pricing is locked. I&apos;ll personally email you the moment Lumetier opens for beta access — usually within 4–6 weeks.
                </p>
                <p className="text-[11px] uppercase tracking-[0.22em] text-stone-600">
                  In the meantime, tell a photographer friend.
                </p>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────────────────────── */}
      <footer className="border-t border-white/[0.07] py-12">
        <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div
            className="text-lg font-light tracking-[0.42em] text-stone-400"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            L U M E T I E R
          </div>
          <div className="flex items-center gap-8 text-[11px] uppercase tracking-[0.22em] text-stone-600">
            <a href="https://allure-image.com" target="_blank" rel="noopener noreferrer" className="hover:text-stone-200 transition-colors">
              Made by Allure Image
            </a>
            <Link to="/lumetier-preview" className="hover:text-stone-200 transition-colors">Brand</Link>
            <Link to="/lumetier/industries" className="hover:text-stone-200 transition-colors">Roadmap</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
